// src/features/forms/SubmissionSlider.jsx
//
// The Submissions page's right-hand slider (SubmissionDetail), opened from
// anywhere that only has a submission row or id — the Work detail's stop
// chips, a Work map pin, a POI's People tab. Loads what SubmissionDetail
// needs (the full submission and its form's fields) and renders it unchanged.
// Portalled to <body>: the Work panel's backdrop-filter makes it the
// containing block for `position: fixed`, which would pin the slider inside it.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { forms as formsApi, formSubmissions as formSubmissionsApi } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';
import SubmissionDetail from './SubmissionDetail';

/**
 * @param {object} props
 * @param {object} [props.submission]   a full row from GET /api/form-submissions
 * @param {string} [props.submissionId] used when only the id is known
 * @param {() => void} props.onClose
 */
export default function SubmissionSlider({ submission: given, submissionId, onClose }) {
  const { t } = useTranslation();
  const id = given?.id ?? submissionId;
  const [loaded, setLoaded] = useState(null); // { id, submission, form }

  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const submission = given?.answers ? given : await formSubmissionsApi.get(id);
        const form = await formsApi.get(submission.form_id).catch(() => null);
        if (!cancelled) setLoaded({ id, submission, form });
      } catch {
        if (!cancelled) {
          showToast(t('forms.submissions.loadError', 'Could not load the form entry'), { duration: 4000 });
          onClose();
        }
      }
    })();
    return () => { cancelled = true; };
    // `given` is a row from a list the caller re-polls — reloading on every
    // poll would flash the slider; the id is what decides what we show.
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!id) return null;
  if (loaded?.id !== id) {
    return createPortal(
      <>
        <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
        <div className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-white dark:bg-slate-900 border-s border-slate-200 dark:border-slate-700 shadow-xl flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-slate-300" />
        </div>
      </>,
      document.body,
    );
  }
  // A form that failed to load still shows the header — name from the row's join.
  const formTemplate = loaded.form || { name: loaded.submission.form_name, fields: [] };
  return createPortal(
    <SubmissionDetail submission={loaded.submission} formTemplate={formTemplate} onClose={onClose} />,
    document.body,
  );
}
